import { Modal, ModalType, Option, Table, TableRow } from '@code/innovance-react-ui-kit';

import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import { TableWrapper } from 'src/front/components/FormStyles';
import uid from 'uid';
import IdentityModal, { IdentityFormData } from './IdentityModal';

const identityTableHeaders = [
  'general_documentType_label',
  'general_documentSeries_label',
  'general_documentNumber_label',
  'general_issueDate_label',
  'general_issuedBy_label',
  'cardEntry_documentEntry_unitCode_label',
];

interface Props {
  identities: Identity[];
  setIdentities: Dispatch<SetStateAction<Identity[]>>;
  identityTypes: Option[];
  isLoading: boolean;
  isEditable?: boolean;
}

interface Identity extends IdentityFormData {
  isNew?: boolean;
}

const IdentityEntry: FC<Props> = ({
  identities,
  isLoading,
  identityTypes,
  setIdentities,
  isEditable = false,
}) => {
  const { t } = useTranslation();
  const [showIdentityModal, setShowIdentityModal] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const [identityFormValues, setIdentityFormValues] = useState<Identity>();
  const [selectedIdentity, setSelectedIdentity] = useState<TableRow>();

  useEffect(() => {
    if (identityTypes.length > 0) {
      !isLoading && setShowIdentityModal(identities.length < 1);
      setIdentityFormValues({
        identityType: identityTypes.find((identity) => identity.value === 'PS') || null,
        expireDate: null,
        identityDocumentSerialCode: '',
        identityDocumentSerialNumber: '',
        issueDate: null,
        issuedBy: '',
        unitCode: '',
      });
    }
  }, [identities.length, identityTypes, identityTypes.length, isLoading]);

  const closeIdentityModal = (): void => setShowIdentityModal(false);

  const handleIdentitySubmit = (identity: Identity): void => {
    let newIdentities;
    if (identity?.id) {
      newIdentities = identities.map((idt) => (idt.id === identity.id ? identity : idt));
    } else {
      newIdentities = [{ ...identity, id: uid(), isNew: true }, ...identities];
    }
    setIdentities(newIdentities);
    setShowIdentityModal(false);
  };

  const handleDeleteIdentity = (): void => {
    setIdentities(identities.filter((idt) => idt.id !== selectedIdentity?.id));
    setSelectedIdentity(undefined);
    setIsDeleteModalOpen(false);
  };

  const addIdentity = useCallback((): void => {
    setIdentityFormValues(undefined);
    setShowIdentityModal(true);
  }, []);

  const editIdentity = useCallback(
    (item?: TableRow): void => {
      setIdentityFormValues(identities.find((idt) => idt.id === item?.id));
      setShowIdentityModal(true);
    },
    [identities]
  );

  const deleteIdentity = useCallback((item?: TableRow): void => {
    setIsDeleteModalOpen(true);
  }, []);

  const identityActions = useMemo(
    () => ({
      add: addIdentity,
      edit: editIdentity,
      delete: deleteIdentity,
    }),
    [addIdentity, deleteIdentity, editIdentity]
  );

  const identityTableData: TableRow[] = identities.map(
    (identity: Identity): TableRow => ({
      id: identity.id || '',
      columns: [
        identity.identityType?.label || '',
        identity.identityDocumentSerialCode,
        identity.identityDocumentSerialNumber,
        formatDate(identity.issueDate),
        identity.issuedBy,
        identity.unitCode,
      ],
      deletable: identity.isNew,
      editable: isEditable || identity.isNew,
    })
  );

  return (
    <>
      {showIdentityModal ? (
        <IdentityModal
          formValues={identityFormValues}
          identityTypes={identityTypes}
          onClose={closeIdentityModal}
          submit={handleIdentitySubmit}
        />
      ) : null}
      {isDeleteModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={t('general_deleteRecordConfirm_text')}
          onCancel={() => setIsDeleteModalOpen((state) => !state)}
          onSubmit={handleDeleteIdentity}
          submitText={t('general_components_confirm_button')}
          title="Confirmation"
          type={ModalType.Confirmation}
        />
      ) : null}
      <TableWrapper>
        <Table
          actionConfig={identityActions}
          addActionText={t('general_add_button')}
          emptyTableText={t('loanApp_documentEntry_noDocumentInfoFound_text')}
          headers={identityTableHeaders.map((head) => t(head))}
          items={identityTableData}
          loading={isLoading}
          onSelect={setSelectedIdentity}
          pageNumber={1}
          selectedItem={selectedIdentity}
          widths={['25', '12', '17', '17', '14', '15']}
        />
      </TableWrapper>
    </>
  );
};

export default IdentityEntry;
